import { lazy, type ComponentType } from "react"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageModule = { default: ComponentType<any> }

const RELOAD_GUARD_KEY = "chunk-reloads"
const RELOAD_GUARD_MS = 60_000
const MAX_RELOADS_PER_WINDOW = 3

// What browsers say when a module could not be fetched (Chromium, Firefox,
// Safari) plus Vite's own message for a lazy stylesheet. Anything else is a bug
// inside the page and must reach the error screen untouched.
const CHUNK_LOAD_ERROR = /dynamically imported module|importing a module script failed|unable to preload css/i

// Automatic reloads are bounded twice so they can never loop: once per failing
// chunk and three in total per minute. The total is above one because on a
// network that drops requests at random each reload may trip on a different
// file, and a second or third attempt is what gets the page through.
function reloadOnce(chunk: string): boolean {
	try {
		const now = Date.now()
		const recent = (JSON.parse(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? "[]") as { chunk: string; at: number }[])
			.filter((r) => now - r.at < RELOAD_GUARD_MS)
		if (recent.length >= MAX_RELOADS_PER_WINDOW || recent.some((r) => r.chunk === chunk)) return false
		window.sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify([...recent, { chunk, at: now }]))
	} catch {
		// Without sessionStorage there is no guard: do not risk a reload loop.
		return false
	}
	window.location.reload()
	return true
}

/**
 * A route chunk can fail to download: a proxy that drops the request, a
 * connection cut mid-transfer, or a deploy that replaced the hashed files under
 * an open tab. Recover before the user sees the error screen:
 *   1. import the same file under a second URL. Calling the import again is not
 *      enough: Chromium remembers that a module URL failed and never asks the
 *      network for it again in that document;
 *   2. reload the page, which also picks up a new deploy. This is the only way
 *      out when the file that failed is a dependency of the page chunk: its URL
 *      is written inside the chunk, so step 1 asks for the same dead URL again.
 */
export async function importWithRetry(factory: () => Promise<PageModule>): Promise<PageModule> {
	try {
		return await factory()
	} catch (err) {
		const message = err instanceof Error ? err.message : ""
		if (!CHUNK_LOAD_ERROR.test(message)) throw err
		// Chromium and Firefox name the failed module URL in the message.
		const url = /https?:\/\/[^\s'")]+/.exec(message)?.[0]
		const sameOrigin = url !== undefined && url.startsWith(`${window.location.origin}/`)
		if (sameOrigin) {
			try {
				// A fixed suffix (not a timestamp) keeps the rescued copy cacheable.
				const mod = (await import(/* @vite-ignore */ `${url}${url.includes("?") ? "&" : "?"}retry=1`)) as Partial<PageModule>
				// Only a chunk whose single export is the page itself. If the bundler
				// ever packs several pages into one file, each is exported under a
				// generated name and `default` could be a different page.
				if (mod.default && Object.keys(mod).length === 1) return mod as PageModule
			} catch {
				// fall through to the reload
			}
		}
		// The page is reloading: stay on the loader instead of flashing the error.
		if (reloadOnce(sameOrigin ? new URL(url).pathname : message)) return new Promise<PageModule>(() => {})
		throw err
	}
}

export function lazyWithRetry(factory: () => Promise<PageModule>) {
	return lazy(() => importWithRetry(factory))
}
